/* eslint-disable react/prop-types */
/* global globalThis */
import CloseIcon from "@mui/icons-material/Close";
import MenuIcon from "@mui/icons-material/Menu";
import {
  AppBar,
  Avatar,
  Box,
  IconButton,
  Toolbar,
  Typography,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import useMediaQuery from "@mui/material/useMediaQuery";
import {
  Suspense,
  lazy,
  useEffect,
  useLayoutEffect,
  useRef
} from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  Navigate,
  Route,
  Routes,
  useLocation,
  useNavigate,
} from "react-router-dom";
import "./App.css";
import Loader from "./Components/Loader";
import NotificationBell from "./Components/NotificationBell";
import SideNavigation from "./Components/SideNav";
import StudentFeesView from "./Components/StudentFeesView";
import ThemeToggle from "./Components/ThemeToggle";
import TokenManager from "./Components/TokenManager";
import { logoutSession } from "./Services/session";
import useNotificationStream from "./hooks/useNotificationStream";
import { logout, selectIsAuthenticated } from "./store/slices/authSlice";
import { fetchSchoolProfile } from "./store/slices/schoolProfileSlice";
import { setSideNavOpen } from "./store/slices/uiSlice";

const AddEmployee = lazy(() => import("./Pages/AddEmployee"));
const AddStudent = lazy(() => import("./Pages/AddStudent"));
const Admin = lazy(() => import("./Pages/Admin"));
const Attendance = lazy(() => import("./Pages/Attendance"));
const ChangePassword = lazy(() => import("./Pages/ChangePassword"));
const AttendanceCalendar = lazy(
  () => import("./Pages/Attendance/AttendanceCalendar"),
);
const HolidayManagement = lazy(
  () => import("./Pages/Attendance/HolidayManagement"),
);
const MarkDailyAttendance = lazy(
  () => import("./Pages/Attendance/MarkDailyAttendance"),
);
const StudentAttendanceHistory = lazy(
  () => import("./Pages/Attendance/StudentAttendanceHistory"),
);
const StaffLeaveManagement = lazy(
  () => import("./Pages/Attendance/StaffLeaveManagement"),
);
const ConfigurationManagement = lazy(() => import("./Pages/Configuration"));
const CourseConfig = lazy(() => import("./Pages/CourseConfig"));
const CourseConfigDetail = lazy(() => import("./Pages/CourseConfigDetail"));
const Dashboard = lazy(() => import("./Pages/Dashboard"));
const EditEmployee = lazy(() => import("./Pages/EditEmployee"));
const EditStudent = lazy(() => import("./Pages/EditStudent"));
const Employees = lazy(() => import("./Pages/Employees"));
const EmployeeDetails = lazy(() => import("./Pages/EmployeeDetails"));
const FeesManagement = lazy(() => import("./Pages/FeesManagement"));
const FeeStructureDetails = lazy(() => import("./Pages/FeeStructureDetails"));
const FeeStructureManagement = lazy(
  () => import("./Pages/FeeStructureManagement"),
);
const ForgotPassword = lazy(() => import("./Pages/ForgotPassword"));
const LoginPage = lazy(() => import("./Pages/LoginPage"));
const ResetPassword = lazy(() => import("./Pages/ResetPassword"));
const StudentAttendanceView = lazy(
  () => import("./Pages/StudentAttendanceView"),
);
const StudentDetails = lazy(() => import("./Pages/StudentDetails"));
const StudentMarks = lazy(() => import("./Pages/StudentMarks"));
const StudentPromotion = lazy(() => import("./Pages/StudentPromotion"));
const StudentPromotionHistory = lazy(
  () => import("./Pages/StudentPromotionHistory"),
);
const Students = lazy(() => import("./Pages/Students"));
const Teachers = lazy(() => import("./Pages/Teachers"));
const TeacherSubjectAssignment = lazy(
  () => import("./Pages/TeacherSubjectAssignment"),
);
const RolePermissionManagement = lazy(
  () => import("./Pages/Admin/RolePermissionManagement"),
);
const AccessControlManagement = lazy(
  () => import("./Pages/Admin/AccessControlManagement"),
);
const EduBot = lazy(() => import("./Pages/EduBot"));
const ExamsGradingManagement = lazy(
  () => import("./Pages/Admin/ExamsGradingManagement"),
);
const AdminExamSetup = lazy(
  () => import("./Pages/Admin/ExamsGrading/ExamSetup"),
);
const AdminGradingSetup = lazy(
  () => import("./Pages/Admin/ExamsGrading/GradingSetup"),
);
const ExamsDashboard = lazy(() => import("./Pages/Exams/Exams"));
const CreateExamPage = lazy(() => import("./Pages/Exams/CreateExamPage"));
const MarksEntry = lazy(() => import("./Pages/Exams/MarksEntry"));
const ReportCardsManagement = lazy(
  () => import("./Pages/Admin/ReportCards/ReportCardsManagement"),
);
const ReportCardView = lazy(
  () => import("./Pages/Admin/ReportCards/ReportCardView"),
);
const AdminLeaveManagement = lazy(
  () => import("./Pages/Admin/LeaveManagement"),
);
const TimetableManagement = lazy(
  () => import("./Pages/Admin/TimetableManagement"),
);
const EventsCalendar = lazy(() => import("./Pages/EventsCalendar"));
const AdmissionsDashboard = lazy(() => import("./Pages/Admissions/AdmissionsDashboard"));
const FinancialManagement = lazy(
  () => import("./Pages/Financial/FinancialManagement"),
);
const MyHrSelfService = lazy(() => import("./Pages/MyHrSelfService"));
const MyTimetable = lazy(() => import("./Pages/MyTimetable"));

const ProtectedRoute = ({ element }) => {
  const isAuthenticated = useSelector(selectIsAuthenticated);
  const user = useSelector((state) => state.auth.user);
  const location = useLocation();

  if (!isAuthenticated) return <Navigate to="/" replace />;

  // Redirect to change password if required (except if already on change-password page)
  if (user?.mustChangePassword && location.pathname !== "/change-password") {
    return <Navigate to="/change-password" replace />;
  }

  return element;
};

const PUBLIC_AUTH_PATHS = new Set(["/", "/forgot-password", "/reset-password"]);

const isPublicAuthPath = (pathname) => {
  if (PUBLIC_AUTH_PATHS.has(pathname)) return true;

  // Tokenized password reset links are served from /reset-password/:token
  return pathname.startsWith("/reset-password/");
};

function App() {
  const location = useLocation();
  const navigate = useNavigate();
  const isAuthenticated = useSelector(selectIsAuthenticated);
  const user = useSelector((state) => state.auth.user);
  const schoolProfile = useSelector((state) => state.schoolProfile.profile);
  const isPublicAuthPage = isPublicAuthPath(location.pathname);
  const showAppChrome = isAuthenticated && !isPublicAuthPage;
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const dispatch = useDispatch();
  const sideNavOpen = useSelector((state) => state.ui.sideNavOpen);
  const sideNavCollapsed = useSelector((state) => state.ui.sideNavCollapsed);
  const hasFetchedProfile = useRef(false);
  const pageContentRef = useRef(null);

  const drawerWidth = sideNavCollapsed ? 64 : 240;
  const displayName =
    String(user?.name || user?.username || "User").trim() || "User";
  const userRole = user?.role || "User";
  const userInitial = displayName.charAt(0).toUpperCase() || "U";
  const fallbackLogo = `${process.env.PUBLIC_URL}/ischool-logo.png`;
  const resolveLogoSrc = (value) => {
    if (!value || typeof value !== "string") return fallbackLogo;
    if (value.startsWith("data:") || value.startsWith("blob:")) return value;
    if (/^https?:\/\//i.test(value)) {
      // Prevent mixed content: upgrade insecure absolute URLs on HTTPS pages.
      if (
        globalThis.location.protocol === "https:" &&
        value.toLowerCase().startsWith("http://")
      ) {
        return value.replace(/^http:\/\//i, "https://");
      }
      return value;
    }
    if (value.startsWith("/")) return value;
    return `/${value.replace(/^\/+/, "")}`;
  };
  const schoolLogoSrc = resolveLogoSrc(schoolProfile?.logo);
  const handleLogout = async () => {
    await logoutSession();
    dispatch(logout());
    navigate("/");
  };

  useNotificationStream();

  useEffect(() => {
    if (isAuthenticated && !hasFetchedProfile.current) {
      hasFetchedProfile.current = true;
      dispatch(fetchSchoolProfile());
    } else if (!isAuthenticated) {
      // Reset the ref when user logs out
      hasFetchedProfile.current = false;
    }
  }, [isAuthenticated, dispatch]);

  useLayoutEffect(() => {
    const resetScrollPosition = () => {
      if (pageContentRef.current) {
        pageContentRef.current.scrollTop = 0;
        pageContentRef.current.scrollLeft = 0;
      }

      globalThis.scrollTo(0, 0);
      document.documentElement.scrollTop = 0;
      document.body.scrollTop = 0;
    };

    resetScrollPosition();
    const frameId = globalThis.requestAnimationFrame(resetScrollPosition);

    return () => {
      globalThis.cancelAnimationFrame(frameId);
    };
  }, [location.key]);

  return (
    <div className="App" style={{ display: "flex", flexDirection: "row" }}>
      <TokenManager />
      {showAppChrome && (
        <div style={{ width: isMobile ? 0 : drawerWidth }}>
          <SideNavigation onLogout={handleLogout} />
        </div>
      )}
      <Box
        sx={{
          flex: 1,
          minWidth: 0,
          height: "100vh",
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
        }}
      >
        {showAppChrome && (
          <AppBar
            position="sticky"
            color="default"
            elevation={0}
            sx={{ top: 0, zIndex: (muiTheme) => muiTheme.zIndex.drawer + 1 }}
          >
            <Toolbar
              sx={{
                minHeight: { xs: 60, md: 64 },
                px: { xs: 0.75, sm: 1.5, md: 2 },
                gap: 1,
              }}
            >
              {isMobile && (
                <IconButton
                  edge="start"
                  color="inherit"
                  aria-label={sideNavOpen ? "close drawer" : "open drawer"}
                  onClick={() => dispatch(setSideNavOpen(!sideNavOpen))}
                >
                  {sideNavOpen ? <CloseIcon /> : <MenuIcon />}
                </IconButton>
              )}
              {isMobile && (
                <Box sx={{ display: "flex", alignItems: "center", ml: 0.5 }}>
                  <img
                    src={schoolLogoSrc}
                    alt="iSchool Logo"
                    width="34"
                    height="34"
                    style={{ objectFit: "contain", borderRadius: 6 }}
                    onError={(e) => {
                      if (e.currentTarget.src !== fallbackLogo) {
                        e.currentTarget.src = fallbackLogo;
                      }
                    }}
                  />
                </Box>
              )}
              <Box sx={{ flexGrow: 1 }} />
              <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
                <ThemeToggle />
                <NotificationBell />
              </Box>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                  ml: 1,
                  px: 0.6,
                  py: 0.35,
                }}
              >
                <Avatar
                  sx={{
                    width: 32,
                    height: 32,
                    fontSize: 14,
                    bgcolor: "primary.main",
                    color: "primary.contrastText",
                  }}
                >
                  {userInitial}
                </Avatar>
                {!isMobile && (
                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      maxWidth: 180,
                    }}
                  >
                    <Typography
                      variant="body2"
                      sx={{ lineHeight: 1.1, fontWeight: 600 }}
                      noWrap
                    >
                      {displayName}
                    </Typography>
                    <Typography variant="caption" color="text.secondary" noWrap>
                      {userRole}
                    </Typography>
                  </Box>
                )}
              </Box>
            </Toolbar>
          </AppBar>
        )}
        <div
          ref={pageContentRef}
          style={{
            padding: isPublicAuthPage ? 0 : isMobile ? 8 : 16,
            maxWidth: "100%",
            overflowX: "hidden",
            overflowY: "auto",
            flex: 1,
          }}
        >
          <Suspense fallback={<Loader />}>
            <Routes>
              <Route path="/" element={<LoginPage />} />
              <Route path="/forgot-password" element={<ForgotPassword />} />
              <Route path="/reset-password" element={<ResetPassword />} />
              <Route
                path="/reset-password/:token"
                element={<ResetPassword />}
              />
              <Route
                path="/change-password"
                element={<ProtectedRoute element={<ChangePassword />} />}
              />
              <Route
                path="/dashboard"
                element={<ProtectedRoute element={<Dashboard />} />}
              />
              <Route
                path="/student"
                element={<ProtectedRoute element={<Students />} />}
              />
              <Route
                path="/student/add"
                element={<ProtectedRoute element={<AddStudent />} />}
              />
              <Route
                path="/student/:id/edit"
                element={<ProtectedRoute element={<EditStudent />} />}
              />
              <Route
                path="/student/:id/view"
                element={<ProtectedRoute element={<StudentDetails />} />}
              />
              <Route
                path="/student/:id/fees/view"
                element={<ProtectedRoute element={<StudentFeesView />} />}
              />
              <Route
                path="/student/:id/attendance"
                element={<ProtectedRoute element={<StudentAttendanceView />} />}
              />
              <Route
                path="/student/:id/marks"
                element={<ProtectedRoute element={<StudentMarks />} />}
              />
              <Route
                path="/student/promotion/history/:id"
                element={
                  <ProtectedRoute element={<StudentPromotionHistory />} />
                }
              />
              <Route
                path="/admin"
                element={<ProtectedRoute element={<Admin />} />}
              />
              <Route
                path="/admin/configuration"
                element={
                  <ProtectedRoute element={<ConfigurationManagement />} />
                }
              />
              <Route
                path="/admin/access-control"
                element={
                  <ProtectedRoute element={<AccessControlManagement />} />
                }
              />
              <Route
                path="/admin/exams-grading"
                element={
                  <ProtectedRoute element={<ExamsGradingManagement />} />
                }
              />
              <Route
                path="/admin/exams-grading/exams"
                element={<ProtectedRoute element={<AdminExamSetup />} />}
              />
              <Route
                path="/admin/exams-grading/grading-setup"
                element={<ProtectedRoute element={<AdminGradingSetup />} />}
              />
              <Route
                path="/admin/report-cards"
                element={<ProtectedRoute element={<ReportCardsManagement />} />}
              />
              <Route
                path="/admin/report-cards/:reportCardId"
                element={<ProtectedRoute element={<ReportCardView />} />}
              />
              <Route
                path="/admin/leave-management"
                element={<ProtectedRoute element={<AdminLeaveManagement />} />}
              />
              <Route
                path="/admin/timetable"
                element={<ProtectedRoute element={<TimetableManagement />} />}
              />
              <Route
                path="/admin/role-permissions"
                element={
                  <ProtectedRoute element={<RolePermissionManagement />} />
                }
              />
              <Route
                path="/admin/promotion"
                element={<ProtectedRoute element={<StudentPromotion />} />}
              />
              <Route
                path="/admin/course-config"
                element={<ProtectedRoute element={<CourseConfig />} />}
              />
              <Route
                path="/admin/course-config/course/:courseId"
                element={<ProtectedRoute element={<CourseConfigDetail />} />}
              />
              <Route
                path="/admin/teacher-subject-assignment"
                element={
                  <ProtectedRoute element={<TeacherSubjectAssignment />} />
                }
              />
              <Route
                path="/admin/attendance"
                element={<ProtectedRoute element={<Attendance />} />}
              />
              <Route
                path="/attendance/mark"
                element={<ProtectedRoute element={<MarkDailyAttendance />} />}
              />
              <Route
                path="/attendance/calendar"
                element={<ProtectedRoute element={<AttendanceCalendar />} />}
              />
              <Route
                path="/attendance/history"
                element={
                  <ProtectedRoute element={<StudentAttendanceHistory />} />
                }
              />
              <Route
                path="/attendance/holidays"
                element={<ProtectedRoute element={<HolidayManagement />} />}
              />
              <Route
                path="/events-calendar"
                element={<ProtectedRoute element={<EventsCalendar />} />}
              />
              <Route
                path="/admissions"
                element={<ProtectedRoute element={<AdmissionsDashboard />} />}
              />
              <Route
                path="/attendance/staff-leave"
                element={<ProtectedRoute element={<StaffLeaveManagement />} />}
              />
              <Route
                path="/teacher"
                element={<ProtectedRoute element={<Teachers />} />}
              />
              <Route
                path="/employee"
                element={<ProtectedRoute element={<Employees />} />}
              />
              <Route
                path="/employee/add"
                element={<ProtectedRoute element={<AddEmployee />} />}
              />
              <Route
                path="/employee/:id/edit"
                element={<ProtectedRoute element={<EditEmployee />} />}
              />
              <Route
                path="/employee/:id/view"
                element={<ProtectedRoute element={<EmployeeDetails />} />}
              />
              <Route
                path="/fees"
                element={<ProtectedRoute element={<FeesManagement />} />}
              />
              <Route
                path="/exams"
                element={<ProtectedRoute element={<ExamsDashboard />} />}
              />
              <Route
                path="/exams/create"
                element={<ProtectedRoute element={<CreateExamPage />} />}
              />
              <Route
                path="/exams/edit/:examId"
                element={<ProtectedRoute element={<CreateExamPage />} />}
              />
              <Route
                path="/exams/grading"
                element={<Navigate to="/exams?tab=grading" replace />}
              />
              <Route
                path="/exams/marks-entry"
                element={<ProtectedRoute element={<MarksEntry />} />}
              />
              <Route
                path="/financial"
                element={<ProtectedRoute element={<FinancialManagement />} />}
              />
              <Route
                path="/my-hr"
                element={<ProtectedRoute element={<MyHrSelfService />} />}
              />
              <Route
                path="/my-timetable"
                element={<ProtectedRoute element={<MyTimetable />} />}
              />
              {process.env.REACT_APP_ENABLE_EDUBOT === "true" && (
                <Route
                  path="/edubot"
                  element={<ProtectedRoute element={<EduBot />} />}
                />
              )}
              <Route
                path="/fee-structure-management"
                element={
                  <ProtectedRoute element={<FeeStructureManagement />} />
                }
              />
              <Route
                path="/fee-structure-details/:courseId/:academicYear/:classYear"
                element={<ProtectedRoute element={<FeeStructureDetails />} />}
              />
              <Route
                path="*"
                element={isAuthenticated ? <Dashboard /> : <LoginPage />}
              />
            </Routes>
          </Suspense>
        </div>
      </Box>
    </div>
  );
}

export default App;
